import { useEffect, useRef, useState } from "react"
import { CandidateDocumentsTab } from "@/modules/recruitment/candidates/ui/components/CandidateDocumentsTab"
import { CommentThread } from "@/modules/recruitment/applications/ui/components/comments/CommentThread"
import { ScorecardForm } from "@/modules/recruitment/applications/ui/components/ScorecardForm"
import { ScorecardPanelComparison } from "@/modules/recruitment/applications/ui/components/ScorecardPanelComparison"
import { GetApplicationDetails } from "../../applicants-service"
import { ApplicationDetails } from "../../dtos/ApplicationDetails"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { Badge } from "@/modules/shared/ui/components/badge"
import { getInterviewStatusLabel } from "@/modules/recruitment/utils/enumHelpers"
import { InterviewStatus } from "@/modules/recruitment/enums/InterviewStatus"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/modules/shared/ui/components/tabs"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Button } from "@/modules/shared/ui/components/button"
import {
  ExternalLink,
  FileText,
  Phone,
  Mail,
  Briefcase,
  GraduationCap,
  PlayCircle,
  Calendar,
  ArrowLeft,
  Check,
  X,
  BarChart3,
  ClipboardList,
  Sparkles,
  Video,
  FolderOpen,
  Eye,
  EyeOff,
  Copy,
  Bot,
  Users,
  Fingerprint,
  Loader2,
  ShieldCheck,
} from "lucide-react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { CreateExpertInterviewDialog } from "../components/CreateExpertInterviewDialog"
import { CreateVideoInterviewDialog } from "../components/CreateVideoInterviewDialog"
import { AcceptCandidateDialog } from "../components/AcceptCandidateDialog"
import { RejectCandidateDialog } from "../components/RejectCandidateDialog"
import { IdVerificationDialog } from "../components/IdVerificationDialog"
import { DebugAiCallDialog } from "../components/DebugAiCallDialog"
import { useApplicants } from "../hooks/useApplicants"
import { useRescoreApplication } from "@/modules/recruitment/hooks/useApplications"
import { ScoringStatusBadge } from "../components/ScoringStatusBadge"
import { InfoTooltip } from "@/modules/shared/ui/components/info-tooltip"
import { TooltipWrap } from "@/modules/shared/ui/components/tooltip-wrap"
import { useAiScreeningCall } from "../hooks/useAiScreeningCall"
import { useOpenCandidateCv } from "@/modules/recruitment/candidates/hooks/useOpenCandidateCv"
import { useUserProfile } from "@/modules/profile/hooks/useProfile"
import {
  hireCandidate,
  rejectCandidate,
  reconcileInterview,
} from "../../applicants-service"
import { parseApplicationStage } from "../../dtos/ApplicationDetails"
import { ApplicationStage } from "../../enums/CandidateStatus"
import { InterviewType } from "../../enums/InterviewType"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/modules/shared/ui/components/tooltip"
import { EmployeeFormValues } from "@/modules/onboarding/ui/components/EmployeeForm"
import { InterviewQuestion } from "@/modules/public/dto/videoInterviewDto"
import axios from "axios"
import { toast } from "sonner"
import { format } from "date-fns"
import { useDispatch, useSelector } from "react-redux"
import type { AppDispatch } from "@/store"
import {
  fetchVacancyById,
  selectVacancies,
} from "@/modules/recruitment/vacancies/store/vacancySlice"
import { CompareVacancyDialog } from "@/modules/recruitment/suggestions/ui/components/CompareVacancyDialog"
import { VacancyComparisonPanel } from "@/modules/recruitment/suggestions/ui/components/VacancyComparisonPanel"
import { SuggestCandidatePanel } from "@/modules/recruitment/suggestions/ui/components/SuggestCandidatePanel"
import { SuggestionsList } from "@/modules/recruitment/suggestions/ui/components/SuggestionsList"
import type { VacancySuggestionDto } from "@/modules/recruitment/suggestions/dtos/vacancy-suggestion.dto"
import { ArrowLeftRight } from "lucide-react"
import { AccessPanel } from "@/modules/resource-access/ui/components/AccessPanel"
import { useCanManageAccess } from "@/modules/resource-access/hooks/useCanManageAccess"

function MaskedField({ value }: { value: string | undefined | null }) {
  const [visible, setVisible] = useState(false)

  if (!value) {
    return <span className="text-muted-foreground italic">Not provided</span>
  }

  const masked = value.slice(0, 3) + value.slice(3).replace(/./g, "•")

  const handleCopy = () => {
    navigator.clipboard.writeText(value)
    toast.success("Copied to clipboard")
  }

  return (
    <div className="flex items-center gap-1">
      <span className="text-foreground font-semibold text-lg truncate">
        {visible ? value : masked}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => setVisible((v) => !v)}
        className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleCopy}
        className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
      >
        <Copy className="w-4 h-4" />
      </Button>
    </div>
  )
}

export const ApplicantDetailsPage = () => {
  const { applicationId } = useParams<{ applicationId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { currentOrganization, organizations, setOrganizationById } =
    useOrganizationsService()
  const {
    scheduleExpertInterview,
    createVideoInterview,
  } = useApplicants()
  const { startAiScreeningCall, isStarting: isStartingScreening } =
    useAiScreeningCall()
  const { data: userProfile } = useUserProfile()
  const { openCv, openingCandidateId } = useOpenCandidateCv()
  const dispatch = useDispatch<AppDispatch>()
  const vacancies = useSelector(selectVacancies)
  const [details, setDetails] = useState<ApplicationDetails | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const highlightCommentId = searchParams.get("commentId") ?? undefined
  const linkedOrganizationId = searchParams.get("organizationId")
  const orgSwitchPending =
    !!linkedOrganizationId && linkedOrganizationId !== currentOrganization?.id

  useEffect(() => {
    if (
      !linkedOrganizationId ||
      linkedOrganizationId === currentOrganization?.id
    )
      return
    if (organizations.length === 0) return

    const isMember = organizations.some((o) => o.id === linkedOrganizationId)
    if (isMember) {
      setOrganizationById(linkedOrganizationId)
    } else {
      setError(
        "You don't have access to the organisation this link belongs to.",
      )
    }
  }, [
    linkedOrganizationId,
    currentOrganization?.id,
    organizations,
    setOrganizationById,
  ])
  const rescoreMutation = useRescoreApplication()

  const handleRescore = async () => {
    if (!details || !currentOrganization?.id) return
    try {
      const updated = await rescoreMutation.mutateAsync({
        organizationId: currentOrganization.id,
        applicationId: details.id,
      })
      setDetails((prev) => (prev ? { ...prev, ...updated } : prev))
    } catch {
      // toast handled inside the hook
    }
  }
  const [activeTab, setActiveTab] = useState(
    highlightCommentId ? "notes" : "match-analysis",
  )
  const [isExpertInterviewDialogOpen, setIsExpertInterviewDialogOpen] =
    useState(false)
  const [isScheduling, setIsScheduling] = useState(false)
  const [isVideoInterviewDialogOpen, setIsVideoInterviewDialogOpen] =
    useState(false)
  const [isCreatingVideoInterview, setIsCreatingVideoInterview] =
    useState(false)
  const [isAcceptDialogOpen, setIsAcceptDialogOpen] = useState(false)
  const [isAccepting, setIsAccepting] = useState(false)
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [isIdVerificationDialogOpen, setIsIdVerificationDialogOpen] =
    useState(false)
  const [isPhoneRequiredDialogOpen, setIsPhoneRequiredDialogOpen] =
    useState(false)
  const [isCompareDialogOpen, setIsCompareDialogOpen] = useState(false)
  const [isAccessPanelOpen, setIsAccessPanelOpen] = useState(false)
  const canManageAccess = useCanManageAccess()
  const canEdit = details ? details.myAccessLevel !== "Read" : true
  const [activeSuggestion, setActiveSuggestion] = useState<VacancySuggestionDto | null>(null)
  const [showSuggestPanel, setShowSuggestPanel] = useState(false)
  const comparisonSectionRef = useRef<HTMLDivElement>(null)
  const [now, setNow] = useState(() => Date.now())
  const [isReconciling, setIsReconciling] = useState(false)
  const [lastBgReconcileAt, setLastBgReconcileAt] = useState<number | null>(
    null,
  )
  const [bgReconcileError, setBgReconcileError] = useState(false)

  useEffect(() => {
    if (!activeSuggestion) return
    comparisonSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    toast.success("Comparison ready")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSuggestion?.id, activeSuggestion?.updatedAt])

  const stage = details
    ? parseApplicationStage(details.currentStage)
    : ApplicationStage.Applied
  const isTerminal =
    stage === ApplicationStage.Hired || stage === ApplicationStage.Rejected
  const hasVideoInterview = details?.interviews?.some(
    (i) => i.type === InterviewType[InterviewType.VideoScreening],
  )
  const candidateName = details
    ? `${details.candidate.firstName} ${details.candidate.lastName}`
    : ""

  const handleAiScreenCall = async () => {
    if (!applicationId || !currentOrganization?.id) return
    await startAiScreeningCall({
      applicationId,
      phoneNumber: userProfile?.phoneNumber,
      onPhoneRequired: () => setIsPhoneRequiredDialogOpen(true),
      onSuccess: async () => {
        const updatedData = await GetApplicationDetails(
          currentOrganization.id,
          applicationId,
        )
        setDetails(updatedData)
      },
    })
  }

  const handleVideoInterviewCreate = async (data: {
    questions: Partial<InterviewQuestion>[]
  }) => {
    if (!applicationId) return
    setIsCreatingVideoInterview(true)
    try {
      await createVideoInterview({
        applicationId,
        questions: data.questions,
        maxVideoAttempts: 3,
        videoDeadline: undefined,
      })
      toast.success("Video interview invite sent successfully")
      setIsVideoInterviewDialogOpen(false)
      const updated = await GetApplicationDetails(
        currentOrganization!.id,
        applicationId,
      )
      setDetails(updated)
    } catch {
      toast.error("Failed to create video interview")
    } finally {
      setIsCreatingVideoInterview(false)
    }
  }

  const handleAcceptCandidate = async (
    contractFile: File,
    employeeData: EmployeeFormValues,
  ) => {
    if (!currentOrganization?.id || !details) return
    setIsAccepting(true)
    try {
      await hireCandidate(
        details.id,
        details.candidate.id,
        contractFile,
        employeeData.jobStartDate,
        employeeData.workflowTemplateIds,
        employeeData.documentsNeeded,
      )
      toast.success(`${candidateName} accepted successfully`)
      setIsAcceptDialogOpen(false)
      const updated = await GetApplicationDetails(
        currentOrganization.id,
        applicationId!,
      )
      setDetails(updated)
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error.response?.data?.error ??
            error.response?.data?.message ??
            "Failed to accept candidate",
        )
      } else {
        toast.error("Failed to accept candidate")
      }
    } finally {
      setIsAccepting(false)
    }
  }

  const handleRejectCandidate = async (reason: string) => {
    if (!currentOrganization?.id || !details) return
    setIsRejecting(true)
    try {
      await rejectCandidate(currentOrganization.id, details.id, reason)
      toast.success(`${candidateName} rejected`)
      setIsRejectDialogOpen(false)
      const updated = await GetApplicationDetails(
        currentOrganization.id,
        applicationId!,
      )
      setDetails(updated)
    } catch {
      toast.error("Failed to reject candidate")
    } finally {
      setIsRejecting(false)
    }
  }

  const CreateAiScreeningCallButton: React.FC<{
    title: string
  }> = ({ title }) => {
    return (
      <Button
        size="sm"
        className="bg-purple-600 hover:bg-purple-700 text-white shadow-sm"
        disabled={isStartingScreening || !canEdit}
        onClick={handleAiScreenCall}
      >
        {isStartingScreening ? (
          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
        ) : (
          <Phone className="w-3.5 h-3.5 mr-1.5" />
        )}
        {isStartingScreening ? "Starting call…" : title}
      </Button>
    )
  }

  const AI_SCREENING_INITIAL_WAIT_MS = 90_000
  const AI_SCREENING_RECONCILE_INTERVAL_MS = 20_000
  const inProgressAiScreening = (details?.interviews ?? []).find(
    (i) => i.type === "AiScreening" && i.status === InterviewStatus.InProgress,
  )
  const hasInProgressAiScreening = Boolean(inProgressAiScreening)

  useEffect(() => {
    if (!hasInProgressAiScreening) return
    const id = window.setInterval(() => setNow(Date.now()), 5_000)
    return () => window.clearInterval(id)
  }, [hasInProgressAiScreening])

  useEffect(() => {
    if (!hasInProgressAiScreening) {
      setLastBgReconcileAt(null)
      setBgReconcileError(false)
    }
  }, [hasInProgressAiScreening])

  const runReconcile = async (
    interviewId: string,
    silent: boolean,
  ): Promise<boolean> => {
    if (!currentOrganization?.id || !applicationId) return false
    if (!silent) setIsReconciling(true)
    try {
      await reconcileInterview(currentOrganization.id, interviewId)
      const refreshed = await GetApplicationDetails(
        currentOrganization.id,
        applicationId,
      )
      setDetails(refreshed)
      const stillInProgress =
        refreshed.interviews?.some(
          (i) =>
            i.id === interviewId && i.status === InterviewStatus.InProgress,
        ) ?? false
      setBgReconcileError(false)
      if (!silent) {
        if (stillInProgress) {
          toast.message("Call agent still reports the call as active.")
        } else {
          toast.success("Interview status updated")
        }
      }
      return stillInProgress
    } catch {
      setBgReconcileError(true)
      if (!silent) toast.error("Failed to check call status")
      return true
    } finally {
      if (!silent) setIsReconciling(false)
    }
  }

  useEffect(() => {
    if (!inProgressAiScreening || bgReconcileError || isReconciling) return

    const startedAt = inProgressAiScreening.createdAt
      ? new Date(inProgressAiScreening.createdAt).getTime()
      : now
    const ageMs = now - startedAt
    if (ageMs < AI_SCREENING_INITIAL_WAIT_MS) return

    const sinceLastMs = lastBgReconcileAt ? now - lastBgReconcileAt : Infinity
    if (sinceLastMs < AI_SCREENING_RECONCILE_INTERVAL_MS) return

    const interviewId = inProgressAiScreening.id
    let cancelled = false
    setLastBgReconcileAt(now)
    void (async () => {
      await runReconcile(interviewId, true)
      if (cancelled) return
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inProgressAiScreening?.id,
    inProgressAiScreening?.createdAt,
    now,
    bgReconcileError,
    isReconciling,
    lastBgReconcileAt,
  ])

  const handleReconcileInterview = async (interviewId: string) => {
    await runReconcile(interviewId, false)
  }

  useEffect(() => {
    const fetchDetails = async () => {
      if (!applicationId || !currentOrganization?.id || orgSwitchPending) return

      setIsLoading(true)
      setError(null)
      try {
        const data = await GetApplicationDetails(
          currentOrganization.id,
          applicationId,
        )
        setDetails(data)

        const matchingVacancy = vacancies.find((v) => v.id === data.vacancyId)
        if (!matchingVacancy && currentOrganization?.id) {
          dispatch(
            fetchVacancyById({
              organizationId: currentOrganization.id,
              id: data.vacancyId,
            }),
          )
        }
      } catch (err) {
        console.error("Failed to fetch application details", err)
        setError("Failed to load application details.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchDetails()
  }, [
    applicationId,
    currentOrganization?.id,
    orgSwitchPending,
    vacancies,
    dispatch,
  ])

  const handleExpertInterviewSchedule = async (data: {
    title: string
    candidateInstructions: string
    timeSlots: { startTime: string; endTime: string }[]
  }) => {
    if (!applicationId) return

    setIsScheduling(true)
    try {
      await scheduleExpertInterview({
        applicationId,
        title: data.title,
        candidateInstructions: data.candidateInstructions,
        timeSlots: data.timeSlots,
      })
      toast.success(
        "Expert interview scheduled successfully. Candidate has been notified.",
      )
      setIsExpertInterviewDialogOpen(false)

      const updatedData = await GetApplicationDetails(
        currentOrganization!.id,
        applicationId,
      )
      setDetails(updatedData)
    } catch (error) {
      console.error("Failed to schedule interview", error)
      const message =
        axios.isAxiosError(error) && error.response?.data?.error
          ? error.response.data.error
          : "Failed to schedule interview"
      toast.error(message)
    } finally {
      setIsScheduling(false)
    }
  }

  const readOnlyDescription = "You have read-only access to this candidate"

  const applicantActions = [
    {
      icon: Bot,
      label: "Create Screening Call",
      description: canEdit
        ? "Start an AI-powered phone screening"
        : readOnlyDescription,
      onClick: handleAiScreenCall,
      disabled: !canEdit,
      color: "text-violet-500",
    },
    ...(!hasVideoInterview
      ? [
          {
            icon: Video,
            label: "Create Video Interview",
            description: canEdit
              ? "Send an async video interview invite"
              : readOnlyDescription,
            onClick: () => setIsVideoInterviewDialogOpen(true),
            disabled: !canEdit,
            color: "text-purple-500",
          },
        ]
      : []),
    {
      icon: Users,
      label: "Schedule Expert Interview",
      description: canEdit
        ? "Book a one-on-one with a domain expert"
        : readOnlyDescription,
      onClick: () => setIsExpertInterviewDialogOpen(true),
      disabled: !canEdit,
      color: "text-primary",
    },
    {
      icon: Fingerprint,
      label: "Run ID Verification",
      description: canEdit
        ? "Verify candidate identity via IDAS"
        : readOnlyDescription,
      onClick: () => setIsIdVerificationDialogOpen(true),
      disabled: !canEdit,
      color: "text-teal-500",
    },
    {
      icon: Check,
      label: "Accept Candidate",
      description: canEdit ? "Hire this candidate" : readOnlyDescription,
      onClick: () => setIsAcceptDialogOpen(true),
      disabled: !canEdit,
      color: "text-green-500",
    },
    {
      icon: X,
      label: "Reject Candidate",
      description: canEdit ? "Reject this candidate" : readOnlyDescription,
      onClick: () => setIsRejectDialogOpen(true),
      disabled: !canEdit,
      color: "text-destructive",
    },
    ...(canManageAccess
      ? [
          {
            icon: ShieldCheck,
            label: "Manage Access",
            description: "Control which team members can see this candidate",
            onClick: () => setIsAccessPanelOpen(true),
            disabled: false,
            color: "text-emerald-600",
          },
        ]
      : []),
  ]

  if (isLoading || (orgSwitchPending && !error)) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Spinner className="w-8 h-8 text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-[80vh] flex-col items-center justify-center gap-4 text-center">
        <h2 className="text-xl font-semibold text-foreground">{error}</h2>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    )
  }

  if (!details) {
    return null
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="space-y-8 max-w-7xl mx-auto pb-12 px-4 sm:px-6 lg:px-8 pt-6">
        {/* Header Section */}
        <div className="flex flex-col gap-6">
          <Button
            variant="ghost"
            className="w-fit pl-0 hover:text-primary text-muted-foreground transition-all duration-200"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Applicants
          </Button>

          <div className="bg-card rounded-3xl shadow-lg border border-border p-8 relative overflow-hidden">
            {/* Decorative gradient overlay */}
            <div className="absolute top-0 right-0 w-1/3 h-full bg-linear-to-l from-primary/5 to-transparent opacity-50 rounded-3xl" />

            <div className="relative z-10 flex flex-col md:flex-row justify-between items-start gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-4xl font-bold text-foreground tracking-tight">
                    {details.candidate.firstName} {details.candidate.lastName}
                  </h1>
                  <ScoringStatusBadge
                    score={details.matchScore.score}
                    scoringStatus={details.scoringStatus}
                    scoringFailureReason={details.scoringFailureReason}
                    onRescore={handleRescore}
                    isRescoring={rescoreMutation.isPending}
                    showRescoreButton
                  />
                  {details.myAccessLevel === "Read" && (
                    <Badge variant="outline" className="text-muted-foreground">
                      Read-only access
                    </Badge>
                  )}
                </div>
                <p className="text-muted-foreground mt-3 text-lg flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary" />
                  Applied for{" "}
                  <span className="font-semibold text-foreground">
                    {details.vacancyTitle}
                  </span>
                </p>
              </div>

              <div className="flex items-center gap-3 shrink-0 flex-wrap">
                <TooltipWrap
                  title="Compare with Another Vacancy"
                  description="Score this candidate's CV against a different open vacancy using their stored CV. Running a full comparison uses AI credits from the organization's wallet."
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsCompareDialogOpen(true)}
                    className="flex items-center gap-1.5"
                  >
                    <ArrowLeftRight className="w-3.5 h-3.5" />
                    Compare with another vacancy
                  </Button>
                </TooltipWrap>
                {!isTerminal && (
                  <TooltipProvider>
                    <div className="flex items-center gap-1.5">
                      {applicantActions.map((action, idx) => {
                        const showScreeningSpinner =
                          action.onClick === handleAiScreenCall &&
                          isStartingScreening
                        const isDisabled =
                          action.disabled || showScreeningSpinner
                        const Icon = showScreeningSpinner
                          ? Loader2
                          : action.icon
                        return (
                          <Tooltip key={idx}>
                            <TooltipTrigger asChild>
                              <Button
                                onClick={action.onClick}
                                disabled={isDisabled}
                                className={`w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 border border-white/50 transition-all duration-200 shadow-sm ${
                                  isDisabled
                                    ? "opacity-40 cursor-not-allowed"
                                    : "hover:scale-110 hover:bg-card cursor-pointer"
                                }`}
                              >
                                <Icon
                                  className={`w-4 h-4 ${action.color}${showScreeningSpinner ? " animate-spin" : ""}`}
                                />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="bottom"
                              className="max-w-[200px] p-0 overflow-hidden"
                            >
                              <div className="px-3.5 py-2.5 space-y-1">
                                <div className="flex items-center gap-2">
                                  <Icon
                                    className={`w-3.5 h-3.5 shrink-0 ${action.color}`}
                                  />
                                  <span className="text-[13px] font-semibold text-white leading-tight">
                                    {action.label}
                                  </span>
                                </div>
                                <p className="text-[11px] leading-relaxed text-muted-foreground">
                                  {action.description}
                                </p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )
                      })}
                    </div>
                  </TooltipProvider>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Candidate Overview & Skills */}
          <div className="space-y-8">
            <Card className="bg-card shadow-md hover:shadow-lg transition-shadow duration-300 border-border">
              <CardHeader className="bg-linear-to-r from-primary/5 to-transparent border-b border-border">
                <CardTitle className="text-xl text-foreground flex items-center gap-2">
                  <div className="w-1 h-5 bg-primary rounded-full" />
                  Candidate Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 pt-6">
                <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted transition-colors duration-200">
                  <div className="p-2 bg-primary/10 rounded-full">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      Email
                    </p>
                    <MaskedField value={details.candidate.email} />
                  </div>
                </div>

                <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted transition-colors duration-200">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full">
                    <Phone className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      Phone
                    </p>
                    <MaskedField value={details.candidate.phone} />
                  </div>
                </div>

                <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted transition-colors duration-200">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                    <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      Applied Date
                    </p>
                    <p className="text-foreground font-semibold text-lg">
                      {format(new Date(details.appliedAt), "MMM d, yyyy")}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted transition-colors duration-200">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-full">
                    <Briefcase className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      Experience
                    </p>
                    <p className="text-foreground font-semibold text-lg">
                      {details.candidate.experienceYears} Years
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted transition-colors duration-200">
                  <div className="p-2 bg-primary/10 rounded-full">
                    <GraduationCap className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      Education
                    </p>
                    <p className="text-foreground font-semibold text-lg">
                      {details.candidate.education}
                    </p>
                  </div>
                </div>

                {details.candidate.cvUrl && (
                  <div className="pt-4">
                    <Button
                      variant="outline"
                      onClick={() => void openCv(details.candidateId)}
                      disabled={openingCandidateId === details.candidateId}
                      className="w-full justify-between group hover:border-primary/30 hover:bg-primary/5"
                    >
                      <span className="flex items-center">
                        <FileText className="mr-2 size-4 text-muted-foreground group-hover:text-primary" />
                        View CV / Resume
                      </span>
                      <ExternalLink className="ml-2 size-3 text-muted-foreground group-hover:text-primary/80" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {details.candidate.skills.length > 0 && (
              <Card className="bg-card shadow-md hover:shadow-lg transition-shadow duration-300 border-border">
                <CardHeader className="bg-linear-to-r from-muted/50 to-transparent border-b border-border">
                  <CardTitle className="text-xl text-foreground flex items-center gap-2">
                    <div className="w-1 h-5 bg-foreground rounded-full" />
                    Skills
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="flex flex-wrap gap-2">
                    {details.candidate.skills.map((skill, i) => (
                      <Badge
                        key={i}
                        className="bg-foreground/80 hover:bg-foreground/70 text-background px-3 py-1.5 shadow-sm transition-all hover:scale-105"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column: Interviews & Notes */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="bg-card border border-border p-1 rounded-xl shadow-sm mb-6 w-full justify-start h-auto">
                <TabsTrigger
                  value="match-analysis"
                  className="flex-1 py-3 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm rounded-lg transition-all"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Match Analysis
                </TabsTrigger>
                <TabsTrigger
                  value="interviews"
                  className="flex-1 py-3 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm rounded-lg transition-all"
                >
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Interviews & Screenings
                </TabsTrigger>
                <TabsTrigger
                  value="scorecard"
                  className="flex-1 py-3 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm rounded-lg transition-all"
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Scorecard
                </TabsTrigger>
                <TabsTrigger
                  value="notes"
                  className="flex-1 py-3 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm rounded-lg transition-all"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Notes & Activity
                </TabsTrigger>
                <TabsTrigger
                  value="documents"
                  className="flex-1 py-3 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm rounded-lg transition-all"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Documents
                </TabsTrigger>
              </TabsList>

              <TabsContent value="match-analysis" className="mt-0">
                <div className="space-y-6">
                  {/* Score Overview */}
                  <Card className="bg-card shadow-md border-border overflow-hidden">
                    <CardHeader className="bg-primary/10 border-b border-border overflow-visible">
                      <div className="flex items-center justify-between flex-wrap gap-2 mt-2">
                        <CardTitle className="text-lg text-foreground flex items-center gap-2">
                          <div className="w-1 h-5 bg-primary rounded-full" />
                          Score Breakdown
                        </CardTitle>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-muted-foreground text-xs whitespace-nowrap">
                            Scored{" "}
                          </span>
                          <Badge className="bg-primary/20 text-primary border-primary/30 text-lg px-4 py-1 whitespace-nowrap">
                            {details.matchScore.score}%
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-6">
                      {(() => {
                        const categoryOrder = [
                          "RequiredSkills",
                          "OptionalSkills",
                          "KeyResponsibilities",
                          "Certification",
                        ]
                        const categoryConfig: Record<
                          string,
                          { label: string; badgeClass: string }
                        > = {
                          RequiredSkills: {
                            label: "Required Skills",
                            badgeClass: "bg-red-50 text-red-700 border-red-200",
                          },
                          OptionalSkills: {
                            label: "Optional Skills",
                            badgeClass:
                              "bg-primary/5 text-primary border-primary/20",
                          },
                          KeyResponsibilities: {
                            label: "Key Responsibilities",
                            badgeClass:
                              "bg-amber-50 text-amber-700 border-amber-200",
                          },
                          Certification: {
                            label: "Certifications",
                            badgeClass:
                              "bg-purple-50 text-purple-700 border-purple-200",
                          },
                        }

                        const grouped = (
                          details.matchScore?.skillBreakdown ?? []
                        ).reduce<
                          Record<
                            string,
                            typeof details.matchScore.skillBreakdown
                          >
                        >((acc, skill) => {
                          const cat = skill.category || "Other"
                          if (!acc[cat]) acc[cat] = []
                          acc[cat].push(skill)
                          return acc
                        }, {})

                        const sortedCategories = [
                          ...categoryOrder.filter((c) => grouped[c]),
                          ...Object.keys(grouped).filter(
                            (c) => !categoryOrder.includes(c),
                          ),
                        ]

                        return sortedCategories.map((category) => {
                          const config = categoryConfig[category] || {
                            label: category,
                            badgeClass:
                              "bg-muted text-foreground border-border",
                          }
                          const skills = grouped[category]

                          return (
                            <div key={category} className="space-y-3">
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className={`text-[11px] px-2 py-0.5 font-semibold border ${config.badgeClass}`}
                                >
                                  {config.label}
                                </Badge>
                                <span className="text-[10px] text-muted-foreground">
                                  {skills.length} item
                                  {skills.length !== 1 ? "s" : ""}
                                </span>
                              </div>
                              <div className="grid gap-2">
                                {skills.map((skill, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center gap-4 rounded-lg border border-border bg-muted/30 px-4 py-3 hover:bg-muted transition-colors"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm font-medium text-foreground">
                                          {skill.skillName}
                                        </span>
                                      </div>
                                      <p className="text-xs text-muted-foreground">
                                        {skill.explanation}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-4 shrink-0">
                                      <div className="w-32">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-[10px] text-muted-foreground">
                                            Match
                                          </span>
                                          <span className="text-xs font-semibold text-foreground">
                                            {skill.matchPercentage}%
                                          </span>
                                        </div>
                                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                          <div
                                            className={`h-full rounded-full transition-all ${
                                              skill.matchPercentage >= 80
                                                ? "bg-green-500"
                                                : skill.matchPercentage >= 60
                                                  ? "bg-yellow-500"
                                                  : "bg-red-400"
                                            }`}
                                            style={{
                                              width: `${skill.matchPercentage}%`,
                                            }}
                                          />
                                        </div>
                                      </div>
                                      <div className="text-right w-16">
                                        <div className="text-[10px] text-muted-foreground">
                                          Weight
                                        </div>
                                        <div className="text-xs font-medium text-foreground">
                                          {skill.normalizedWeight.toFixed(1)}%
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        })
                      })()}
                    </CardContent>
                  </Card>

                  {/* CV Tailoring Analysis */}
                  {details.matchScore?.tailoringSignals && (
                    <Card className="bg-card shadow-md border-border">
                      <CardHeader className="bg-linear-to-r from-amber-50/60 to-transparent dark:from-amber-900/10 border-b border-border pb-3">
                        <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
                          <Bot className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                          CV Tailoring Analysis
                          {details.matchScore?.aiProbabilityScore !==
                            undefined &&
                            details.matchScore.aiProbabilityScore > 0 && (
                              <span
                                className={`ml-auto inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold ${
                                  details.matchScore.aiProbabilityScore >= 0.7
                                    ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                    : details.matchScore.aiProbabilityScore >=
                                        0.4
                                      ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                                      : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                }`}
                              >
                                {details.matchScore.aiProbabilityScore >= 0.7
                                  ? "High"
                                  : details.matchScore.aiProbabilityScore >= 0.4
                                    ? "Medium"
                                    : "Low"}
                                <span className="opacity-70">
                                  (
                                  {(
                                    details.matchScore.aiProbabilityScore * 100
                                  ).toFixed(0)}
                                  %)
                                </span>
                              </span>
                            )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-5 space-y-5">
                        {/* Exact Phrase Matches */}
                        {details.matchScore.tailoringSignals.exactPhraseMatches
                          .length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-100 text-red-700 text-[10px] font-bold dark:bg-red-900/40 dark:text-red-400">
                                {
                                  details.matchScore.tailoringSignals
                                    .exactPhraseMatches.length
                                }
                              </span>
                              Exact Phrase Matches
                            </p>
                            <div className="space-y-2">
                              {details.matchScore.tailoringSignals.exactPhraseMatches.map(
                                (match, i) => (
                                  <div
                                    key={i}
                                    className="rounded-md border border-red-200 dark:border-red-900/50 bg-red-50/40 dark:bg-red-900/10 px-3 py-2"
                                  >
                                    <p className="text-xs font-medium text-red-800 dark:text-red-300 italic">
                                      "{match.phrase}"
                                    </p>
                                    <p className="text-[10px] text-muted-foreground mt-0.5">
                                      {match.context}
                                    </p>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                        {/* Keyword Overlap */}
                        {details.matchScore.tailoringSignals.keywordOverlap
                          .length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                              Keyword Overlap
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {details.matchScore.tailoringSignals.keywordOverlap.map(
                                (kw, i) => (
                                  <span
                                    key={i}
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                                  >
                                    {kw}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                        {/* Structural Similarity */}
                        {details.matchScore.tailoringSignals
                          .structuralSimilarity && (
                          <div
                            className={`rounded-md border px-3 py-2 ${
                              details.matchScore.tailoringSignals
                                .structuralSimilarity.detected
                                ? "border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-900/10"
                                : "border-green-200 dark:border-green-900/50 bg-green-50/40 dark:bg-green-900/10"
                            }`}
                          >
                            <p
                              className={`text-xs font-semibold flex items-center gap-1.5 mb-0.5 ${
                                details.matchScore.tailoringSignals
                                  .structuralSimilarity.detected
                                  ? "text-amber-800 dark:text-amber-300"
                                  : "text-green-800 dark:text-green-300"
                              }`}
                            >
                              <Sparkles className="w-3.5 h-3.5" />
                              {details.matchScore.tailoringSignals
                                .structuralSimilarity.detected
                                ? "Structural Mirroring Detected"
                                : "No Structural Mirroring"}
                              {details.matchScore.tailoringSignals
                                .structuralSimilarity.confidence > 0 && (
                                <span className="ml-auto text-[10px] font-medium">
                                  {Math.round(
                                    details.matchScore.tailoringSignals
                                      .structuralSimilarity.confidence * 100,
                                  )}
                                  % confidence
                                </span>
                              )}
                            </p>
                            <p className="text-[10px] text-muted-foreground">
                              {details.matchScore.tailoringSignals
                                .structuralSimilarity.notes ||
                                (details.matchScore.tailoringSignals
                                  .structuralSimilarity.detected
                                  ? "The CV closely follows the structure of the job description."
                                  : "The CV uses a standard chronological format and shows no suspicious structural mirroring.")}
                            </p>
                          </div>
                        )}
                        {/* Tailoring Indicators */}
                        {details.matchScore.tailoringSignals.tailoringIndicators
                          .length > 0 ? (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                              Observed Indicators
                            </p>
                            <ul className="space-y-1">
                              {details.matchScore.tailoringSignals.tailoringIndicators.map(
                                (indicator, i) => (
                                  <li
                                    key={i}
                                    className="text-xs text-foreground flex items-start gap-2"
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                                    {indicator}
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        ) : details.matchScore.aiProbabilityScore !==
                            undefined &&
                          details.matchScore.aiProbabilityScore > 0 ? (
                          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 mt-4">
                            <p className="text-xs font-semibold text-muted-foreground mb-1">
                              AI Assessment
                            </p>
                            <p className="text-[11px] text-muted-foreground">
                              {details.matchScore.aiProbabilityScore >= 0.7
                                ? `The AI assigned a high tailoring risk score of ${(details.matchScore.aiProbabilityScore * 100).toFixed(0)}%. The CV appears to closely mirror the structure, phrasing, or keyword coverage of the job description.`
                                : details.matchScore.aiProbabilityScore >= 0.4
                                  ? `The AI assigned a medium tailoring risk score of ${(details.matchScore.aiProbabilityScore * 100).toFixed(0)}%. The CV shows moderate overlap with the job description — some phrasing and keyword alignment suggests deliberate tailoring.`
                                  : `The AI assigned a low tailoring risk score of ${(details.matchScore.aiProbabilityScore * 100).toFixed(0)}%. The CV appears to be written independently with natural phrasing and authentic work history.`}
                            </p>
                          </div>
                        ) : null}
                      </CardContent>
                    </Card>
                  )}

                  {/* Strengths & Weaknesses */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Card className="bg-card shadow-md border-green-200 dark:border-green-900/40">
                      <CardHeader className="bg-green-50/50 dark:bg-green-900/20 border-b border-green-100 dark:border-green-900/30 pb-3">
                        <CardTitle className="text-sm font-semibold text-green-800 dark:text-green-400 flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                          Strengths
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4">
                        {(details.matchScore?.strengths ?? []).length > 0 ? (
                          <ul className="space-y-2">
                            {(details.matchScore?.strengths ?? []).map(
                              (s, i) => (
                                <li
                                  key={i}
                                  className="text-sm text-foreground flex items-start gap-2"
                                >
                                  <Check className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" />
                                  {s}
                                </li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">
                            No strengths identified
                          </p>
                        )}
                      </CardContent>
                    </Card>
                    <Card className="bg-card shadow-md border-red-200 dark:border-red-900/40">
                      <CardHeader className="bg-red-50/50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-900/30 pb-3">
                        <CardTitle className="text-sm font-semibold text-red-800 dark:text-red-400 flex items-center gap-2">
                          <X className="w-4 h-4 text-red-500 dark:text-red-400" />
                          Weaknesses
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4">
                        {(details.matchScore?.weaknesses ?? []).length > 0 ? (
                          <ul className="space-y-2">
                            {(details.matchScore?.weaknesses ?? []).map(
                              (w, i) => (
                                <li
                                  key={i}
                                  className="text-sm text-foreground flex items-start gap-2"
                                >
                                  <X className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                                  {w}
                                </li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">
                            No weaknesses identified
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="interviews" className="mt-0">
                <div className="space-y-6">
                  {/* AI Screening Summary - Always visible */}
                  {(() => {
                    const aiScreening = (details.interviews ?? []).find(
                      (i) => i.type === "AiScreening",
                    )
                    const isInProgress =
                      aiScreening?.status === InterviewStatus.InProgress
                    return (
                      <Card className="bg-card shadow-md border-purple-200 dark:border-purple-900/40 overflow-hidden">
                        <CardHeader className="bg-linear-to-r from-purple-50 dark:from-purple-900/20 to-transparent border-b border-purple-100 dark:border-purple-900/30 pb-4">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg text-foreground flex items-center gap-2">
                              <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                              </div>
                              AI Screening Summary
                              <InfoTooltip
                                title="AI Screening Summary"
                                maxWidth={280}
                                description="A summary generated after an AI phone screening call with the candidate. The recording and full transcript are available below. Starting a call, or calling again, uses one of the organization's AI call credits."
                              />
                            </CardTitle>
                            {aiScreening?.status ===
                              InterviewStatus.Completed && (
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-900/40"
                                >
                                  AI Generated
                                </Badge>
                                <CreateAiScreeningCallButton title="Call again" />
                              </div>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="pt-5 space-y-4">
                          {aiScreening?.status ===
                            InterviewStatus.Completed && (
                            <>
                              {aiScreening.aiSummary && (
                                <p className="text-muted-foreground leading-relaxed bg-purple-50/30 p-4 rounded-lg border border-purple-100">
                                  {aiScreening.aiSummary}
                                </p> //Following the same pattern as the rest the code. However AI screening summary has been depricated in VAPI,
                                // We should find another solution
                              )}
                              {aiScreening.recordingUrl && (
                                <div className="bg-muted/50 p-4 rounded-xl border border-border">
                                  <div className="flex items-center gap-2 mb-3">
                                    <div className="p-1.5 bg-red-100 rounded-full">
                                      <PlayCircle className="w-4 h-4 text-red-600" />
                                    </div>
                                    <span className="font-semibold text-sm text-foreground">
                                      Screening Recording
                                    </span>
                                  </div>
                                  <audio
                                    controls
                                    className="w-full h-10 accent-primary"
                                  >
                                    <source
                                      src={aiScreening.recordingUrl}
                                      type="audio/wav"
                                    />
                                    Your browser does not support the audio
                                    element.
                                  </audio>
                                </div>
                              )}
                              {aiScreening.transcript && (
                                <div className="space-y-2">
                                  <h4 className="font-semibold text-sm text-foreground flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 bg-purple-500 rounded-full" />
                                    Transcript
                                  </h4>
                                  <div className="bg-card p-4 rounded-lg border border-border text-muted-foreground max-h-60 overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
                                    {aiScreening.transcript}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                          {aiScreening?.status ===
                            InterviewStatus.Incomplete && (
                            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                              <p className="text-sm font-medium text-foreground">
                                AI screening call could not be completed
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                The call did not complete. You can try again.
                              </p>
                              <p className="text-xs text-muted-foreground mt-3">
                                <CreateAiScreeningCallButton title="Try Again" />
                              </p>
                            </div>
                          )}
                          {isInProgress &&
                            (() => {
                              const startedAt = aiScreening?.createdAt
                                ? new Date(aiScreening.createdAt).getTime()
                                : now
                              const ageMs = now - startedAt
                              const passedInitialWait =
                                ageMs >= AI_SCREENING_INITIAL_WAIT_MS
                              const confirmedByAgent =
                                passedInitialWait && lastBgReconcileAt !== null

                              if (bgReconcileError) {
                                return (
                                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                                    <p className="text-sm font-medium text-foreground">
                                      Couldn&apos;t reach the call agent
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1 max-w-sm text-center">
                                      We haven&apos;t received an update and
                                      can&apos;t reach ElevenLabs right now.
                                      Retry, or start a new call.
                                    </p>
                                    <div className="flex items-center gap-2 mt-3">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={isReconciling || !canEdit}
                                        onClick={() =>
                                          aiScreening &&
                                          handleReconcileInterview(
                                            aiScreening.id,
                                          )
                                        }
                                      >
                                        {isReconciling
                                          ? "Checking..."
                                          : "Retry check"}
                                      </Button>
                                      <CreateAiScreeningCallButton title="Try Again" />
                                    </div>
                                  </div>
                                )
                              }

                              return (
                                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                                  <Spinner className="w-6 h-6 text-purple-500 mb-3" />
                                  <p className="text-sm font-medium text-foreground">
                                    AI screening call in progress
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1 max-w-sm text-center">
                                    {confirmedByAgent
                                      ? "Screening calls can take a few minutes. We'll refresh once the call ends."
                                      : "Summary will appear here once the call is complete."}
                                  </p>
                                </div>
                              )
                            })()}
                          {!aiScreening && (
                            <div className="flex flex-col items-center justify-center py-8">
                              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-full mb-3">
                                <Sparkles className="w-6 h-6 text-purple-400 dark:text-purple-500" />
                              </div>
                              <p className="text-sm font-medium text-foreground">
                                No AI screening call yet
                              </p>
                              <p className="text-xs text-muted-foreground mt-1 mb-4">
                                Start a screening call to get an AI-generated
                                summary.
                              </p>
                              <p className="text-xs text-muted-foreground mt-3">
                                <CreateAiScreeningCallButton title="Start Screening Call" />
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )
                  })()}

                  {/* Interview Blocks (exclude AI Screening — shown in summary above) */}
                  {details.interviews
                    .filter((i) => i.type !== "AiScreening")
                    .map((interview) => (
                      <Card
                        key={interview.id}
                        className="bg-card shadow-md hover:shadow-lg transition-all duration-300 border-border overflow-hidden group"
                      >
                        <CardHeader className="bg-muted/30 border-b border-border pb-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge
                                  variant="outline"
                                  className="bg-card border-primary/20 text-primary"
                                >
                                  {interview.type === "AiScreening"
                                    ? "AI Screening"
                                    : interview.type === "ExpertSession"
                                      ? "Expert Interview"
                                      : interview.type}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  •
                                </span>
                                <span className="text-sm text-muted-foreground">
                                  {format(
                                    new Date(interview.createdAt),
                                    "MMM d, yyyy 'at' h:mm a",
                                  )}
                                </span>
                              </div>
                              <CardTitle className="text-lg text-foreground group-hover:text-primary transition-colors">
                                {interview.type === "AiScreening"
                                  ? "AI Screening Call"
                                  : interview.title || "Interview Session"}
                              </CardTitle>
                            </div>
                            <Badge
                              className={`${
                                interview.status === InterviewStatus.Completed
                                  ? "bg-green-100 text-green-700 hover:bg-green-200"
                                  : interview.status === InterviewStatus.Failed
                                    ? "bg-red-100 text-red-700 hover:bg-red-200"
                                    : interview.status ===
                                        InterviewStatus.InProgress
                                      ? "bg-primary/10 text-primary hover:bg-primary/20"
                                      : interview.status ===
                                          InterviewStatus.Scheduled
                                        ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                                        : interview.status ===
                                            InterviewStatus.AwaitingSelection
                                          ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                                          : "bg-muted text-foreground hover:bg-muted"
                              } px-3 py-1 shadow-sm`}
                            >
                              {getInterviewStatusLabel(interview.status)}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-6 pt-6">
                          {interview.type !== "AiScreening" &&
                            interview.recordingUrl && (
                              <div className="bg-muted/50 p-4 rounded-xl border border-border">
                                <div className="flex items-center gap-2 mb-3">
                                  <div
                                    className={`p-1.5 rounded-full ${interview.type === "VideoScreening" ? "bg-primary/10" : "bg-red-100"}`}
                                  >
                                    {interview.type === "VideoScreening" ? (
                                      <Video className="w-4 h-4 text-primary" />
                                    ) : (
                                      <PlayCircle className="w-4 h-4 text-red-600" />
                                    )}
                                  </div>
                                  <span className="font-semibold text-sm text-foreground">
                                    {interview.type === "VideoScreening"
                                      ? "Video Recording"
                                      : "Audio Recording"}
                                  </span>
                                </div>
                                {interview.type === "VideoScreening" ? (
                                  <video
                                    controls
                                    className="w-full rounded-lg aspect-video bg-black"
                                  >
                                    <source
                                      src={interview.recordingUrl}
                                      type="video/webm"
                                    />
                                    <source
                                      src={interview.recordingUrl}
                                      type="video/mp4"
                                    />
                                    Your browser does not support the video
                                    element.
                                  </video>
                                ) : (
                                  <audio
                                    controls
                                    className="w-full h-10 accent-primary"
                                  >
                                    <source
                                      src={interview.recordingUrl}
                                      type="audio/wav"
                                    />
                                    Your browser does not support the audio
                                    element.
                                  </audio>
                                )}
                              </div>
                            )}

                          {interview.timeSlots &&
                            interview.timeSlots.length > 0 && (
                              <div className="space-y-3">
                                <h4 className="font-semibold text-sm text-foreground flex items-center gap-2">
                                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full" />
                                  Status & Schedule
                                </h4>
                                <div className="grid gap-2 sm:grid-cols-2">
                                  {interview.timeSlots.map(
                                    (slot: {
                                      id: string
                                      startTime: string
                                      endTime: string
                                      isSelected: boolean
                                    }) => (
                                      <div
                                        key={slot.id}
                                        className={`p-3 rounded-lg border transition-all ${
                                          slot.isSelected
                                            ? "bg-amber-50 border-amber-200 shadow-sm ring-1 ring-amber-200"
                                            : "bg-card border-border opacity-70"
                                        }`}
                                      >
                                        <div className="flex justify-between items-start mb-1">
                                          <span className="font-semibold text-foreground text-sm">
                                            {format(
                                              new Date(slot.startTime),
                                              "MMM d",
                                            )}
                                          </span>
                                          {slot.isSelected && (
                                            <Badge
                                              variant="secondary"
                                              className="bg-amber-100 text-amber-800 text-[10px] px-1.5 h-5"
                                            >
                                              Confirmed
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          {format(
                                            new Date(slot.startTime),
                                            "h:mm a",
                                          )}{" "}
                                          -{" "}
                                          {format(
                                            new Date(slot.endTime),
                                            "h:mm a",
                                          )}
                                        </div>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}

                          {interview.type !== "AiScreening" && (
                            <div className="flex flex-col gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                              <p className="text-sm text-muted-foreground">
                                Capture interview feedback in the scorecard.
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-11 w-full sm:h-8 sm:w-auto"
                                onClick={() => setActiveTab("scorecard")}
                              >
                                <ClipboardList className="w-3.5 h-3.5 mr-1" />
                                Open scorecard
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </TabsContent>

              <TabsContent value="scorecard" className="mt-0 space-y-6">
                {applicationId &&
                  currentOrganization?.id &&
                  details?.vacancyId && (
                    <>
                      <ScorecardForm
                        organizationId={currentOrganization.id}
                        applicationId={applicationId}
                        vacancyId={details.vacancyId}
                        readOnly={!canEdit}
                      />
                      <ScorecardPanelComparison
                        organizationId={currentOrganization.id}
                        applicationId={applicationId}
                      />
                    </>
                  )}
              </TabsContent>

              <TabsContent value="notes">
                <Card className="bg-card shadow-md border-border">
                  <CardContent className="py-6">
                    {applicationId && currentOrganization?.id && (
                      <CommentThread
                        organizationId={currentOrganization.id}
                        applicationId={applicationId}
                        highlightCommentId={highlightCommentId}
                        readOnly={!canEdit}
                      />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              {details?.candidateId && (
                <TabsContent value="documents" className="mt-0">
                  <CandidateDocumentsTab candidateId={details.candidateId} readOnly={!canEdit} />
                </TabsContent>
              )}
            </Tabs>
          </div>
        </div>
      </div>

      {applicationId && details && (
        <div ref={comparisonSectionRef} className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeSuggestion && (
            <VacancyComparisonPanel
              suggestion={activeSuggestion}
              vacancyATitle={details.vacancyTitle}
              vacancyAScore={details.matchScore}
              onSuggest={() => setShowSuggestPanel(true)}
              onKeep={() => {
                setActiveSuggestion(null)
                setShowSuggestPanel(false)
              }}
              isSuggestDisabled={showSuggestPanel}
            />
          )}
          {showSuggestPanel && activeSuggestion && (
            <SuggestCandidatePanel
              suggestion={activeSuggestion}
              onSuggested={(updated) => {
                setActiveSuggestion(updated)
                setShowSuggestPanel(false)
              }}
              onCancel={() => setShowSuggestPanel(false)}
            />
          )}
          <SuggestionsList
            sourceApplicationId={applicationId}
            candidateId={details.candidateId}
          />
        </div>
      )}

      {details && (
        <AccessPanel
          open={isAccessPanelOpen}
          onClose={() => setIsAccessPanelOpen(false)}
          type="Candidate"
          resourceId={details.candidateId}
          resourceName={`${details.candidate.firstName} ${details.candidate.lastName}`}
        />
      )}

      {applicationId && details && (
        <CompareVacancyDialog
          open={isCompareDialogOpen}
          onClose={() => setIsCompareDialogOpen(false)}
          sourceApplicationId={applicationId}
          candidateId={details.candidateId}
          onCompareComplete={(suggestion) => {
            setActiveSuggestion(suggestion)
            setShowSuggestPanel(false)
          }}
        />
      )}

      <CreateExpertInterviewDialog
        open={isExpertInterviewDialogOpen}
        onOpenChange={setIsExpertInterviewDialogOpen}
        onConfirm={handleExpertInterviewSchedule}
        isSubmitting={isScheduling}
      />
      <CreateVideoInterviewDialog
        open={isVideoInterviewDialogOpen}
        onOpenChange={setIsVideoInterviewDialogOpen}
        onConfirm={handleVideoInterviewCreate}
        isSubmitting={isCreatingVideoInterview}
        vacancyId={details?.vacancyId}
        organizationId={currentOrganization?.id}
      />
      <AcceptCandidateDialog
        open={isAcceptDialogOpen}
        onOpenChange={setIsAcceptDialogOpen}
        onConfirm={handleAcceptCandidate}
        candidateName={candidateName}
        candidateData={
          details
            ? {
                firstName: details.candidate.firstName,
                lastName: details.candidate.lastName,
                email: details.candidate.email,
                jobTitle: details.vacancyTitle,
                documentsNeeded: [],
              }
            : undefined
        }
        formId="accept-candidate-form"
        availableJobs={[]}
        isUploading={isAccepting}
      />
      <RejectCandidateDialog
        open={isRejectDialogOpen}
        onOpenChange={setIsRejectDialogOpen}
        onConfirm={handleRejectCandidate}
        candidateName={candidateName}
        isRejecting={isRejecting}
      />
      <IdVerificationDialog
        open={isIdVerificationDialogOpen}
        onOpenChange={setIsIdVerificationDialogOpen}
        candidateId={details?.candidateId}
        candidateName={candidateName}
        organizationId={currentOrganization?.id}
        prefilledIdNumber={details?.candidate.idNumber ?? undefined}
        onVerificationCompleted={(verifiedCandidateId) => {
          if (details?.candidateId === verifiedCandidateId) {
            setDetails({ ...details, isIdVerificationCompleted: true })
          }
        }}
      />
      <DebugAiCallDialog
        open={isPhoneRequiredDialogOpen}
        onOpenChange={setIsPhoneRequiredDialogOpen}
      />
    </div>
  )
}
